import type { ComponentType } from 'react'
import {
  generationTypesToMarkdown,
  generationsEndpointsMarkdown,
  modelsListToMarkdown,
  parametersToMarkdown,
} from '@/features/docs/services/generation-catalog-markdown.service'

// These 4 docsNavigation slugs aren't backed by a .mdx file — they're
// hand-built React pages (app/src/routes/docs/api-reference/*.tsx) that
// render live data from the generation model catalog. They still need raw
// markdown for the .md-suffix route and the /docs.md full export, so we
// generate it from the same catalog data those pages fetch.
const DYNAMIC_DOC_CONTENT: Record<string, () => string> = {
  'api-reference/generations': generationsEndpointsMarkdown,
  'api-reference/generation-models': modelsListToMarkdown,
  'api-reference/generation-types': generationTypesToMarkdown,
  'api-reference/generation-parameters': parametersToMarkdown,
}

// Raw markdown source, for title/excerpt extraction (getDocTitle/getDocExcerpt
// below) and the table-of-contents heading scan — none of which need a
// rendered component.
//
// Lazy (no `eager: true`): each of the 43 articles becomes its own on-demand
// chunk instead of every article's text shipping in the client's main bundle
// on every page. Loaded per-slug via getDocContent(); still fully available
// during SSR since dynamic import() (unlike @mdx-js/mdx's evaluate(), which
// calls `new Function` at request time) is not blocked on Cloudflare Workers.
const rawContentModules = import.meta.glob<string>('../content/**/*.mdx', {
  query: '?raw',
  import: 'default',
})

// Compiled via the @mdx-js/rollup Vite plugin (vite.config.ts) at BUILD time,
// not runtime. Lazy for the same reason as rawContentModules above — loaded
// per-slug via getDocComponentLoader() and rendered with React.lazy(), which
// TanStack Start's streaming SSR resolves like any other Suspense boundary.
const compiledContentModules = import.meta.glob<{
  default: ComponentType<{ components?: Record<string, unknown> }>
}>('../content/**/*.mdx')

export async function getDocContent(slug: string): Promise<{
  content: string | null
  error: string | null
}> {
  const path = `../content/${slug}.mdx`
  const loadRaw = rawContentModules[path]

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TS types the glob record as total, but an unknown slug is a real runtime case
  if (!loadRaw) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TS types the Record as total, but an unknown slug is a real runtime case
    const dynamicContent = DYNAMIC_DOC_CONTENT[slug]?.()
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see above
    if (dynamicContent !== undefined) {
      return { content: dynamicContent, error: null }
    }
    return { content: null, error: 'Document not found' }
  }

  return { content: await loadRaw(), error: null }
}

/**
 * Returns the (unresolved) dynamic import for a slug's compiled MDX
 * component, for passing straight into React.lazy() — or null if the slug
 * isn't backed by an .mdx file (e.g. the 4 DYNAMIC_DOC_CONTENT pages, which
 * render through their own hand-built route components instead).
 */
export function getDocComponentLoader(
  slug: string,
): (() => Promise<{
  default: ComponentType<{ components?: Record<string, unknown> }>
}>) | null {
  const path = `../content/${slug}.mdx`
  return compiledContentModules[path] ?? null
}

/**
 * Derives a page title for docs content whose slug isn't registered in
 * docsNavigation (so there's no navItem.item.title to fall back on): the
 * first markdown H1 in the content, or a humanized version of the slug.
 */
export function getDocTitle(content: string, slug: string): string {
  if (typeof slug !== 'string') return ''

  const heading = typeof content === 'string' && content.match(/^#\s+(.+)$/m)
  if (heading) return heading[1].trim()

  const lastSegment = slug.split('/').pop() ?? slug
  return lastSegment
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** Strips markdown syntax down to a plain-text excerpt, for meta descriptions. */
export function getDocExcerpt(content: string, maxLength = 155): string {
  if (typeof content !== 'string') return ''

  const plain = content
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#*_>`~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return plain.length > maxLength
    ? `${plain.slice(0, maxLength).trimEnd()}...`
    : plain
}
